// Code-aware error copy for feed mutations. The feed mutation hooks
// (lib/mutations/feed.ts) all funnel non-2xx through `throwApiError`, which
// re-throws a `DomainError` carrying the server's `{ code, message?, details? }`
// envelope. Reading `err.message` surfaced the raw machine code to the user
// (e.g. "feed/empty_post"); this maps the known feed codes to translated copy
// and resolves everything else through the localized `Errors` catalog, then a
// caller-supplied key.
//
// Local to the feed feature on purpose — `lib/api-error.ts` is the shared
// envelope-narrowing surface and stays code-agnostic. The friendly copy is a
// product concern that belongs next to the screens that show it.

import { type useTranslations } from 'use-intl';
import { parseApiErrorCode } from '../../lib/api-error';
import type { DescribeError } from '../../lib/error-message';

type FeedTranslate = ReturnType<typeof useTranslations<'Feed'>>;

// Maps the feed error codes the server emits to their catalog key under
// `errors.codes.*`. Codes absent here fall through to the `Errors` catalog.
const FEED_CODE_KEY = {
  'feed/empty_post': 'errors.codes.feed/empty_post',
  'feed/not_author': 'errors.codes.feed/not_author',
  'feed/already_deleted': 'errors.codes.feed/already_deleted',
  'feed/parent_comment_mismatch': 'errors.codes.feed/parent_comment_mismatch',
  'feed/membership_revoked': 'errors.codes.feed/membership_revoked',
  'feed/attachment_not_visible': 'errors.codes.feed/attachment_not_visible',
  'feed/invalid_media': 'errors.codes.feed/invalid_media',
  'feed/unsupported_media_type': 'errors.codes.feed/unsupported_media_type',
  'feed/idempotency_key_reused': 'errors.codes.feed/idempotency_key_reused',
  NOT_FOUND: 'errors.codes.NOT_FOUND',
  FORBIDDEN: 'errors.codes.FORBIDDEN',
  CONFLICT: 'errors.codes.CONFLICT',
  RATE_LIMITED: 'errors.codes.RATE_LIMITED',
} as const;

// The per-post mention cap the server enforces, echoed back in the 422's
// `details.max` so the copy quotes the real number rather than a client-side
// duplicate of the constant. (`parseApiErrorDetails` types `details` as the
// per-field VALIDATION arrays; this envelope carries a scalar.)
function mentionCap(err: unknown): number | null {
  const max = (
    err as { error?: { details?: { max?: unknown } } } | undefined
  )?.error?.details?.max;
  return typeof max === 'number' ? max : null;
}

// Maps a thrown feed-mutation error to translated user-facing copy. Never
// returns a raw machine code: known feed codes get feed-voiced copy, other
// cataloged codes get the shared `Errors` copy, everything else gets the
// translated `fallbackKey`.
export function feedErrorMessage(
  err: unknown,
  describe: DescribeError,
  t: FeedTranslate,
  fallbackKey: Parameters<FeedTranslate>[0],
): string {
  const code = parseApiErrorCode(err);
  if (code !== null) {
    if (code === 'feed/too_many_mentions') {
      const max = mentionCap(err);
      if (max !== null) {
        return t('errors.codes.feed/too_many_mentions', { max });
      }
      return t(fallbackKey);
    }
    const key = FEED_CODE_KEY[code as keyof typeof FEED_CODE_KEY];
    if (key !== undefined) return t(key);
  }
  return describe(err, t(fallbackKey));
}
